import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import { getTranslate } from 'react-localize-redux'
import {
  Dialog,
  DialogContainer,
  Content,
  Divider,
  Footer,
  Button,
  Heading,
  Text,
  Well,
  TextField,
  Picker,
  Item,
  Checkbox,
} from '@adobe/react-spectrum'
import { SPECTRUM_THEME_CLASS } from '../../themes/spectrum-theme'
import { loadStripe } from '@stripe/stripe-js'
import { saveSubscriptionTerms } from './subscription-terms'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import CheckmarkCircle from '@spectrum-icons/workflow/CheckmarkCircle'
import AlertCircle from '@spectrum-icons/workflow/AlertCircle'
import {
  getPricingDetails,
  updateBillingDetails,
  updateSelectedCurrency,
  initializeSubscriptionCheckout,
  resetSubscriptionCheckout,
  setSubscriptionPaymentProcessing,
  setSubscriptionPaymentSuccess,
  setSubscriptionPaymentError,
  logTermsAcceptance,
} from '../../redux/actions/subscriptionActions'
import { APP_CONFIG } from '../../config'
import styles from './unified-subscription-wizard.styles'

// Stripe.js is initialised once per publishable key. Each seller account has its own key, and a
// Stripe instance cannot be re-pointed at another account, so instances are cached by key.
const stripeInstances = new Map<string, ReturnType<typeof loadStripe>>()
const getStripe = (publishableKey: string) => {
  let instance = stripeInstances.get(publishableKey)
  if (!instance) {
    instance = loadStripe(publishableKey)
    stripeInstances.set(publishableKey, instance)
  }
  return instance
}

const newRequestId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`

// US states for billing address
const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'DC', name: 'District of Columbia' },
]

// Types
interface PricingDetails {
  totalMonthlyPrice: number
  subscriptionTier: string
  activeDeviceCount: number
  deviceBreakdown?: Array<{
    deviceType: string
    deviceCount: number
    pricePerDevice: number
    typeMultiplier: number
    subtotalMonthly: number
  }>
  billingDetails?: BillingDetails
  availableCurrencies?: CurrencyOption[]
  // Countries the server currently sells to, with their seller and permitted currencies
  checkoutCountries?: CheckoutCountry[]
}

interface CheckoutCountry {
  code: string
  name: string
  sellerKey: string
  sellerLegalName: string
  defaultCurrency: string
  currencies: string[]
}

interface BillingDetails {
  email: string
  name: string
  companyName: string
  addressLine1: string
  addressLine2: string
  city: string
  state: string
  postalCode: string
  country: string
  vatNumber: string
}

interface CurrencyOption {
  code: string
  symbol: string
  name: string
}

interface CheckoutState {
  clientSecret: string | null
  stripePublishableKey: string | null
  amountExcludingTax: number
  taxAmount: number
  amountIncludingTax: number
  currency: string
  subscriptionId: string | null
  subscriptionStatus: string | null
  sellerKey: string | null
  sellerLegalName: string | null
  billingCountry: string | null
  loading: boolean
  processing: boolean
  success: boolean
  error: boolean
  errorMessage: string | null
}

interface UnifiedSubscriptionWizardProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  // Redux state — appSettingReducer
  subscriptionPlans: any[]
  supportedCurrencies: any[]
  // Redux state — subscriptionReducer
  pricingDetails: PricingDetails
  billingDetails: BillingDetails
  selectedCurrency: string
  checkout: CheckoutState
  loading: boolean
  error: boolean
  errorMessage: string | null
  // Redux actions
  getPricingDetails: () => void
  updateBillingDetails: (details: Partial<BillingDetails>) => void
  updateSelectedCurrency: (currency: string) => void
  initializeSubscriptionCheckout: (billingDetails: BillingDetails, currency: string, clientRequestId: string) => void
  resetSubscriptionCheckout: () => void
  setSubscriptionPaymentProcessing: () => void
  setSubscriptionPaymentSuccess: () => void
  setSubscriptionPaymentError: (error: string) => void
  logTermsAcceptance: (amount: number, version: string, language: string, subscriptionId?: string | null) => Promise<any>
  selectedLanguage: string
  translate: (key: string, data?: Record<string, unknown>) => string
}

// Helpers
const getCurrencySymbol = (code: string): string => {
  switch (code?.toLowerCase()) {
    case 'gbp': return '£'
    case 'eur': return '€'
    case 'usd': return '$'
    default: return '£'
  }
}

const formatCurrency = (amount: number, currency: string): string => {
  const symbol = getCurrencySymbol(currency)
  return `${symbol}${(amount / 100).toFixed(2)}`
}

const withTimeout = <T,>(operation: Promise<T>, timeoutMs: number, message: string): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), timeoutMs)

    operation.then(
      result => {
        window.clearTimeout(timer)
        resolve(result)
      },
      error => {
        window.clearTimeout(timer)
        reject(error)
      }
    )
  })

const StripeLinkTopLayerGuard: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  useLayoutEffect(() => {
    if (!isActive) return

    const markAsTopLayer = (node: Node) => {
      if (!(node instanceof HTMLElement) || node.closest('[data-overlay-container]')) return

      const stripeFrame = node.matches('iframe[src*="stripe.com"], iframe[name*="stripe"]')
        ? node
        : node.querySelector('iframe[src*="stripe.com"], iframe[name*="stripe"]')

      if (!stripeFrame) return

      let topLayer = node
      while (topLayer.parentElement && topLayer.parentElement !== document.body) {
        topLayer = topLayer.parentElement
      }

      topLayer.dataset.reactAriaTopLayer = 'true'
      topLayer.inert = false
      topLayer.removeAttribute('aria-hidden')
    }

    const observer = new MutationObserver(records => {
      records.forEach(record => record.addedNodes.forEach(markAsTopLayer))
    })

    observer.observe(document.body, { childList: true, subtree: true })
    return () => observer.disconnect()
  }, [isActive])

  return null
}

// ──────────────────────────────────────────────────────────────
// Step 4: Stripe Payment Form
// ──────────────────────────────────────────────────────────────
const PaymentForm: React.FC<{
  checkout: CheckoutState
  onPaymentError: (error: string) => void
  onPaymentSuccess: () => void
  setPaymentProcessing: () => void
  logTermsAcceptance: (amount: number, version: string, language: string, subscriptionId?: string | null) => Promise<any>
  onClose: () => void
  billingEmail: string
  selectedLanguage: string
  translate: (key: string, data?: Record<string, unknown>) => string
}> = ({
  checkout,
  onPaymentError,
  onPaymentSuccess,
  setPaymentProcessing,
  logTermsAcceptance,
  onClose,
  billingEmail,
  selectedLanguage,
  translate,
}) => {
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [termsVersion, setTermsVersion] = useState<string>('v1.0')
  const stripe = useStripe()
  const elements = useElements()

  // Fetch the published terms version for the user's language
  useEffect(() => {
    const lang = selectedLanguage || 'en-GB'
    fetch(`${APP_CONFIG.baseURL}terms/latest?languageCode=${encodeURIComponent(lang)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.version) setTermsVersion(data.version)
      })
      .catch(() => { /* keep default v1.0 */ })
  }, [selectedLanguage])

  const handlePayment = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements || !checkout.clientSecret) {
      onPaymentError(translate('subscription_wizard_payment_system_unavailable'))
      return
    }

    if (!termsAccepted) {
      onPaymentError(translate('subscription_wizard_accept_terms_error'))
      return
    }

    setPaymentProcessing()

    try {
      const timeoutMessage = translate('subscription_wizard_payment_system_unavailable')
      const { error: submitError } = await withTimeout(elements.submit(), 30000, timeoutMessage)

      if (submitError) {
        onPaymentError(submitError.message || translate('subscription_wizard_payment_submit_failed'))
        return
      }

      await withTimeout(
        saveSubscriptionTerms(() => logTermsAcceptance(checkout.amountIncludingTax, termsVersion, selectedLanguage || 'en-GB', checkout.subscriptionId),
          translate('subscription_wizard_payment_submit_failed')),
        30000,
        timeoutMessage
      )
      const { error, paymentIntent } = await withTimeout(
        stripe.confirmPayment({
          elements,
          clientSecret: checkout.clientSecret,
          redirect: 'if_required',
        }),
        90000,
        timeoutMessage
      )

      if (error) {
        onPaymentError(error.message || translate('subscription_wizard_payment_failed'))
      } else if (paymentIntent && (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing')) {
        onPaymentSuccess()
      } else {
        onPaymentError(translate('subscription_wizard_payment_status', { status: paymentIntent?.status || '' }))
      }
    } catch (err: any) {
      onPaymentError(err.message || translate('subscription_wizard_payment_failed'))
    }
  }

  return (
    <form onSubmit={handlePayment}>
      {/* Payment Summary */}
      <Well UNSAFE_style={styles.infoWell}>
        <Heading level={4} marginTop={0} marginBottom="size-100">
          {translate('subscription_wizard_payment_summary')}
        </Heading>
        {checkout.sellerLegalName && (
          <Text UNSAFE_style={styles.mutedLabel}>
            {translate('subscription_wizard_sold_by', { seller: checkout.sellerLegalName })}
          </Text>
        )}
        <div style={styles.summaryRows}>
          <div style={styles.summaryRow}>
            <Text>{translate('subscription_wizard_subtotal_excl_tax')}</Text>
            <Text>{formatCurrency(checkout.amountExcludingTax, checkout.currency)}</Text>
          </div>
          <div style={styles.summaryRow}>
            <Text>{translate('subscription_wizard_tax')}</Text>
            <Text>{formatCurrency(checkout.taxAmount, checkout.currency)}</Text>
          </div>
          <div style={styles.summaryTotalRow}>
            <Text UNSAFE_style={styles.textBold}>{translate('subscription_wizard_monthly_total')}</Text>
            <Text UNSAFE_style={styles.totalAmount}>
              {formatCurrency(checkout.amountIncludingTax, checkout.currency)}
            </Text>
          </div>
        </div>
      </Well>

      {/* Payment error */}
      {checkout.errorMessage && (
        <Well UNSAFE_style={styles.errorWell}>
          <div style={styles.errorContent}>
            <AlertCircle color="negative" size="S" UNSAFE_style={{ marginTop: '2px', flexShrink: 0 }} />
            <Text UNSAFE_style={styles.errorText}>{checkout.errorMessage}</Text>
          </div>
        </Well>
      )}

      {/* Stripe Payment Element */}
      <Well UNSAFE_style={styles.paymentMethodWell}>
        <Text marginBottom="size-200" UNSAFE_style={styles.mutedLabel}>
          {translate('subscription_wizard_payment_method')}
        </Text>
        <div style={styles.paymentElementWrapper}>
          <PaymentElement
            options={{
              layout: 'auto',
              paymentMethodOrder: ['card', 'paypal', 'apple_pay', 'google_pay'],
              wallets: { applePay: 'auto', googlePay: 'auto' },
              defaultValues: billingEmail ? { billingDetails: { email: billingEmail } } : undefined,
            }}
            onLoadError={() => onPaymentError(translate('subscription_wizard_payment_system_unavailable'))}
          />
        </div>
      </Well>

      {/* Terms warning */}
      <Well UNSAFE_style={styles.warningWell}>
        <Text UNSAFE_style={styles.termsText}>
          {translate('subscription_wizard_terms_notice', {
            amount: formatCurrency(checkout.amountIncludingTax, checkout.currency),
          })}
        </Text>
      </Well>

      {/* Terms checkbox */}
      <div style={styles.termsCheckboxRow}>
        <Checkbox
          isSelected={termsAccepted}
          onChange={setTermsAccepted}
          isDisabled={checkout.processing}
        >
          {translate('subscription_wizard_agree_to')}{' '}
        </Checkbox>
        <a
          href={`/terms?lang=${encodeURIComponent(selectedLanguage || 'en-GB')}`}
          target="_blank"
          rel="noopener noreferrer"
          style={styles.termsLink}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        >
          {translate('terms_and_conditions')}
        </a>
      </div>

      {/* Actions */}
      <div style={styles.formActions}>
        <Button variant="secondary" onPress={onClose} isDisabled={checkout.processing}>
          {translate('general_cancel')}
        </Button>
        <Button
          variant="cta"
          type="submit"
          isDisabled={checkout.processing || !checkout.clientSecret || !termsAccepted}
        >
          {checkout.processing
            ? translate('subscription_wizard_processing_payment')
            : translate('subscription_wizard_pay_amount', { amount: formatCurrency(checkout.amountIncludingTax, checkout.currency) })}
        </Button>
      </div>
    </form>
  )
}

// ──────────────────────────────────────────────────────────────
// Main Wizard Component
// ──────────────────────────────────────────────────────────────
const UnifiedSubscriptionWizardComponent: React.FC<UnifiedSubscriptionWizardProps> = ({
  open,
  onClose,
  onSuccess,
  subscriptionPlans,
  supportedCurrencies: supportedCurrenciesFromStore,
  pricingDetails,
  billingDetails,
  selectedCurrency,
  checkout,
  loading,
  error,
  errorMessage,
  getPricingDetails,
  updateBillingDetails,
  updateSelectedCurrency,
  initializeSubscriptionCheckout,
  resetSubscriptionCheckout,
  setSubscriptionPaymentProcessing,
  setSubscriptionPaymentSuccess,
  setSubscriptionPaymentError,
  logTermsAcceptance,
  selectedLanguage,
  translate,
}) => {
  const [step, setStep] = useState(1)
  const [countdown, setCountdown] = useState(5)

  // Fetch pricing details on open
  useEffect(() => {
    if (open) {
      getPricingDetails()
      resetSubscriptionCheckout()
      setStep(1)
    }
  }, [open, getPricingDetails, resetSubscriptionCheckout])

  // The Stripe instance follows the publishable key of the seller chosen by the server for this checkout
  const stripePromise = useMemo(
    () => (checkout.stripePublishableKey ? getStripe(checkout.stripePublishableKey) : null),
    [checkout.stripePublishableKey]
  )

  // Countdown on success
  useEffect(() => {
    if (open && checkout.success) {
      setCountdown(5)
    }
  }, [open, checkout.success])

  useEffect(() => {
    if (open && checkout.success && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    } else if (open && checkout.success && countdown === 0) {
      onSuccess()
    }
  }, [open, checkout.success, countdown, onSuccess])

  const handleNextStep = async () => {
    if (step === 3) {
      initializeSubscriptionCheckout(billingDetails, selectedCurrency, newRequestId())
    }
    if (step < 4) {
      setStep(step + 1)
    }
  }

  const handlePrevStep = () => {
    if (step > 1) {
      setStep(step - 1)
    }
  }

  const handleClose = () => {
    // A payment being confirmed must not be abandoned by dismissing the dialog
    if (checkout.processing) return
    setStep(1)
    resetSubscriptionCheckout()
    onClose()
  }

  const handleBillingChange = (field: keyof BillingDetails, value: string) => {
    updateBillingDetails({ [field]: value })
  }

  const progressPercent = (step / 4) * 100
  const symbol = getCurrencySymbol(selectedCurrency)

  // Only countries the server currently sells to are offered; there is no browser-side fallback list
  const countries: CheckoutCountry[] = pricingDetails?.checkoutCountries || []
  const selectedCountry = countries.find((c) => c.code === billingDetails.country)

  // Currencies permitted for the selected country
  const knownCurrencies: CurrencyOption[] = (supportedCurrenciesFromStore?.length
    ? supportedCurrenciesFromStore
    : pricingDetails?.availableCurrencies || []
  ).map((c: any) => ({ code: (c.code || '').toUpperCase(), symbol: c.symbol, name: c.name }))
  const availableCurrenciesForCountry: CurrencyOption[] = (selectedCountry?.currencies || []).map((code) => {
    const upper = code.toUpperCase()
    return knownCurrencies.find((c) => c.code === upper) || { code: upper, symbol: getCurrencySymbol(code), name: upper }
  })

  // Keep the currency within what the selected country allows
  useEffect(() => {
    if (selectedCountry && !selectedCountry.currencies.includes((selectedCurrency || '').toLowerCase())) {
      const target = (selectedCountry.defaultCurrency || selectedCountry.currencies[0] || '').toUpperCase()
      if (target && target !== selectedCurrency) updateSelectedCurrency(target)
    }
  }, [selectedCountry?.code, selectedCurrency])

  // Build dynamic pricing grid from Plus plan in Redux
  const plusPlan = subscriptionPlans?.find((p: any) => p.id === 'plus')
  const pricingByCurrency: Record<string, Record<string, { price1: number; price2to5: number; price6plus: number }>> = {}
  if (plusPlan?.pricing) {
    for (const p of plusPlan.pricing) {
      const currKey = (p.currency || '').toUpperCase()
      if (!pricingByCurrency[currKey]) pricingByCurrency[currKey] = {}
      pricingByCurrency[currKey][p.deviceCategory] = {
        price1: p.price1Device,
        price2to5: p.price2To5,
        price6plus: p.price6Plus,
      }
    }
  }

  const deviceCategories: string[] = plusPlan?.pricing?.length
    ? [...new Set(plusPlan.pricing.map((p: any) => p.deviceCategory))] as string[]
    : ['LanTEK IV', 'SignalTEK 10G & SignalTEK QT & Pro', 'VDV II Range & PoE PRO']

  const displayCurrency = pricingByCurrency[selectedCurrency?.toUpperCase()]
    ? selectedCurrency.toUpperCase()
    : pricingByCurrency['GBP']
      ? 'GBP'
      : Object.keys(pricingByCurrency)[0] || 'GBP'

  const displaySymbol = getCurrencySymbol(displayCurrency)

  const isBillingValid =
    billingDetails.name?.trim() &&
    billingDetails.addressLine1?.trim() &&
    billingDetails.city?.trim() &&
    billingDetails.postalCode?.trim() &&
    selectedCountry

  return (
    <>
      <StripeLinkTopLayerGuard isActive={open && step === 4} />
      <DialogContainer onDismiss={handleClose}>
        {open && (
        <Dialog size="L" UNSAFE_style={{ width: step === 4 ? '600px' : '800px' }} UNSAFE_className={SPECTRUM_THEME_CLASS}>
          <>
            {/* Loading state */}
            {loading && step < 4 ? (
              <Content>
                <div style={styles.centeredContainer}>
                  <Text>{translate('subscription_wizard_loading_pricing')}</Text>
                  <div style={styles.progressBarTrack}>
                    <div style={styles.progressBarFillAnimated} />
                  </div>
                </div>
              </Content>
            ) : checkout.success ? (
              // Success state
              <Content>
                <div style={styles.centeredContainer}>
                  <CheckmarkCircle color="positive" size="XXL" />
                  <Heading level={2} marginTop="size-300">{translate('subscription_wizard_payment_success')}</Heading>
                  <Text UNSAFE_style={styles.successMessage}>
                    {translate('subscription_wizard_success_description')}
                  </Text>
                  <Text UNSAFE_style={styles.countdownText}>
                    {translate('subscription_wizard_closing_in_seconds', { count: countdown })}
                  </Text>
                  <Button variant="cta" marginTop="size-300" onPress={onSuccess}>
                    {translate('subscription_wizard_close_now')}
                  </Button>
                </div>
              </Content>
            ) : (
              <>
                <Heading level={2}>
                  {step === 1 && translate('subscription_wizard_upgrade_to_plus')}
                  {step === 2 && translate('subscription_wizard_price_breakdown')}
                  {step === 3 && translate('subscription_wizard_billing_details')}
                  {step === 4 && translate('subscription_wizard_complete_payment')}
                </Heading>
                <Divider />

                {/* Error state */}
                {error && step < 4 && (
                  <Content>
                    <Well UNSAFE_style={styles.errorWellCompact}>
                      <Text UNSAFE_style={styles.errorText}>
                        <strong>{translate('subscription_wizard_error')}</strong> {errorMessage}
                      </Text>
                    </Well>
                  </Content>
                )}

                <Content>
                  <div style={styles.stepContainer}>
                    {/* Progress indicator (not shown on step 4) */}
                    {step < 4 && (
                      <div>
                        <Text UNSAFE_style={styles.stepIndicator}>{translate('subscription_wizard_step_of_total', { step, total: 4 })}</Text>
                        <div style={styles.progressBarTrackSmall}>
                          <div style={{ height: '100%', backgroundColor: '#2196F3', width: `${progressPercent}%`, transition: 'width 0.3s ease' }} />
                        </div>
                      </div>
                    )}

                    {/* STEP 1: Pricing Explanation */}
                    {step === 1 && (
                      <Well UNSAFE_style={styles.sectionWell}>
                        <div style={styles.sectionHeader}>
                          <div>
                            <Text UNSAFE_style={styles.sectionTitle}>
                              {translate('subscription_wizard_pricing_question', { plan: plusPlan?.name || 'Plus' })}
                            </Text>
                            <Text UNSAFE_style={styles.sectionDescription}>
                              {translate('subscription_wizard_pricing_explanation', { plan: plusPlan?.name || 'Plus' })}
                            </Text>
                          </div>
                          {Object.keys(pricingByCurrency).length > 1 && (
                            <Picker
                              aria-label={translate('currency')}
                              selectedKey={displayCurrency}
                              onSelectionChange={(key) => updateSelectedCurrency(key as string)}
                              width="140px"
                              isQuiet
                            >
                              {Object.keys(pricingByCurrency).map((curr) => (
                                <Item key={curr}>{`${curr} (${getCurrencySymbol(curr)})`}</Item>
                              ))}
                            </Picker>
                          )}
                        </div>

                        {/* Dynamic pricing grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: `1fr repeat(${deviceCategories.length}, 1fr)`, gap: '8px', alignItems: 'center' }}>
                          <Text UNSAFE_style={styles.textBold}>{translate('subscription_wizard_tier_device')}</Text>
                          {deviceCategories.map((cat) => (
                            <Text key={cat} UNSAFE_style={styles.textBold}>{cat}</Text>
                          ))}

                          <Text>{translate('subscription_wizard_one_device')}</Text>
                          {deviceCategories.map((cat) => {
                            const p = pricingByCurrency[displayCurrency]?.[cat]
                            return <Text key={`1-${cat}`}>{p ? translate('subscription_wizard_per_month', { amount: `${displaySymbol}${(p.price1 / 100).toFixed(2)}` }) : '—'}</Text>
                          })}

                          <Text>{translate('subscription_wizard_two_to_five_devices')}</Text>
                          {deviceCategories.map((cat) => {
                            const p = pricingByCurrency[displayCurrency]?.[cat]
                            return <Text key={`2-${cat}`}>{p ? translate('subscription_wizard_per_month_each', { amount: `${displaySymbol}${(p.price2to5 / 100).toFixed(2)}` }) : '—'}</Text>
                          })}

                          <Text>{translate('subscription_wizard_six_or_more_devices')}</Text>
                          {deviceCategories.map((cat) => {
                            const p = pricingByCurrency[displayCurrency]?.[cat]
                            return <Text key={`6-${cat}`}>{p ? translate('subscription_wizard_per_month_each', { amount: `${displaySymbol}${(p.price6plus / 100).toFixed(2)}` }) : '—'}</Text>
                          })}
                        </div>

                        <Text UNSAFE_style={styles.pricingNote}>
                          {translate('subscription_wizard_prices_note', { currency: displayCurrency })}
                        </Text>
                      </Well>
                    )}

                    {/* STEP 2: Price Breakdown */}
                    {step === 2 && (
                      <>
                        {pricingDetails?.deviceBreakdown?.length > 0 && (
                          <Well UNSAFE_style={styles.sectionWell}>
                            <Text UNSAFE_style={styles.deviceTitle}>
                              {translate('subscription_wizard_registered_devices', { count: pricingDetails.activeDeviceCount })}
                            </Text>
                            {pricingDetails.deviceBreakdown.map((breakdown, idx) => (
                              <div key={idx} style={styles.breakdownRow}>
                                <div style={styles.breakdownRowInner}>
                                  <Text UNSAFE_style={styles.mutedText}>{breakdown.deviceType}</Text>
                                  <Text UNSAFE_style={styles.textBold}>
                                    {translate('subscription_wizard_device_count', { count: breakdown.deviceCount })}
                                  </Text>
                                </div>
                                <Text UNSAFE_style={styles.breakdownDetail}>
                                  {translate('subscription_wizard_monthly_breakdown', {
                                    price: `${symbol}${breakdown.pricePerDevice.toFixed(2)}`,
                                    count: breakdown.deviceCount,
                                    total: `${symbol}${breakdown.subtotalMonthly.toFixed(2)}`,
                                  })}
                                </Text>
                              </div>
                            ))}
                            <div style={styles.totalSection}>
                              <div style={styles.summaryRow}>
                                <Text UNSAFE_style={styles.textBold}>{translate('subscription_wizard_total_before_tax')}</Text>
                                <Text UNSAFE_style={styles.totalValueBlue}>
                                  {translate('subscription_wizard_per_month', { amount: `${symbol}${pricingDetails.totalMonthlyPrice.toFixed(2)}` })}
                                </Text>
                              </div>
                            </div>
                          </Well>
                        )}

                        {(!pricingDetails?.deviceBreakdown || pricingDetails.deviceBreakdown.length === 0) && (
                          <Well UNSAFE_style={styles.warningWellOrange}>
                            <Text UNSAFE_style={styles.warningTextOrange}>
                              {translate('subscription_wizard_no_devices')}
                            </Text>
                          </Well>
                        )}
                      </>
                    )}

                    {/* STEP 3: Billing Details */}
                    {step === 3 && (
                      <>
                        <Well UNSAFE_style={styles.sectionWell}>
                          <Text UNSAFE_style={styles.sectionTitle}>
                            {translate('billing_information')}
                          </Text>

                          <div style={styles.billingGrid}>
                            <TextField
                              label={translate('email')}
                              value={billingDetails.email}
                              isReadOnly
                              isDisabled
                              width="100%"
                            />

                            <Picker
                              label={translate('subscription_wizard_billing_currency')}
                              selectedKey={selectedCurrency}
                              onSelectionChange={(key) => updateSelectedCurrency(key as string)}
                              isDisabled={availableCurrenciesForCountry.length < 2}
                              width="100%"
                            >
                              {availableCurrenciesForCountry.map((curr) => (
                                <Item key={curr.code}>{curr.name} ({curr.symbol})</Item>
                              ))}
                            </Picker>

                            <TextField
                              label={translate('subscription_wizard_full_name')}
                              value={billingDetails.name}
                              onChange={(value) => handleBillingChange('name', value)}
                              isRequired
                              width="100%"
                            />

                            <TextField
                              label={translate('subscription_wizard_company_name')}
                              value={billingDetails.companyName}
                              onChange={(value) => handleBillingChange('companyName', value)}
                              width="100%"
                            />

                            <TextField
                              label={translate('subscription_wizard_address_line_one')}
                              value={billingDetails.addressLine1}
                              onChange={(value) => handleBillingChange('addressLine1', value)}
                              isRequired
                              width="100%"
                            />

                            <TextField
                              label={translate('subscription_wizard_address_line_two')}
                              value={billingDetails.addressLine2}
                              onChange={(value) => handleBillingChange('addressLine2', value)}
                              width="100%"
                            />

                            <TextField
                              label={translate('city')}
                              value={billingDetails.city}
                              onChange={(value) => handleBillingChange('city', value)}
                              isRequired
                              width="100%"
                            />

                            <Picker
                              label={translate('country')}
                              selectedKey={billingDetails.country}
                              onSelectionChange={(key) => {
                                handleBillingChange('country', key as string)
                                if (key !== billingDetails.country) {
                                  handleBillingChange('state', '')
                                }
                              }}
                              isRequired
                              width="100%"
                              description={selectedCountry
                                ? translate('subscription_wizard_sold_by', { seller: selectedCountry.sellerLegalName })
                                : translate('subscription_wizard_country_seller_hint')}
                            >
                              {countries.map((country) => (
                                <Item key={country.code}>{country.name}</Item>
                              ))}
                            </Picker>

                            {billingDetails.country === 'US' ? (
                              <Picker
                                label={translate('state')}
                                selectedKey={billingDetails.state}
                                onSelectionChange={(key) => handleBillingChange('state', key as string)}
                                isRequired
                                width="100%"
                              >
                                {US_STATES.map((s) => (
                                  <Item key={s.code}>{s.name}</Item>
                                ))}
                              </Picker>
                            ) : (
                              <TextField
                                label={translate('subscription_wizard_state_province')}
                                value={billingDetails.state}
                                onChange={(value) => handleBillingChange('state', value)}
                                width="100%"
                              />
                            )}

                            <TextField
                              label={translate('subscription_wizard_postal_code')}
                              value={billingDetails.postalCode}
                              onChange={(value) => handleBillingChange('postalCode', value)}
                              isRequired
                              width="100%"
                            />

                            <TextField
                              label={translate('subscription_wizard_vat_number')}
                              value={billingDetails.vatNumber}
                              onChange={(value) => handleBillingChange('vatNumber', value)}
                              width="100%"
                              description={translate('subscription_wizard_vat_description')}
                            />
                          </div>
                        </Well>

                        {countries.length === 0 && (
                          <Well UNSAFE_style={styles.warningWellOrange}>
                            <Text UNSAFE_style={styles.warningTextOrange}>
                              {translate('subscription_wizard_no_countries_available')}
                            </Text>
                          </Well>
                        )}

                        <Well UNSAFE_style={styles.successWell}>
                          <Text UNSAFE_style={styles.successText}>
                            <strong>{translate('subscription_wizard_estimated_price')}</strong> {translate('subscription_wizard_per_month', { amount: `${symbol}${pricingDetails?.totalMonthlyPrice?.toFixed(2)}` })} {translate('subscription_wizard_applicable_taxes')}
                          </Text>
                        </Well>
                      </>
                    )}

                    {/* STEP 4: Stripe Payment */}
                    {step === 4 && (
                      <>
                        {checkout.loading ? (
                          <div style={styles.centeredContainer}>
                            <Text>{translate('subscription_wizard_preparing_checkout')}</Text>
                            <div style={styles.progressBarTrack}>
                              <div style={{ ...styles.progressBarFillAnimated, width: '70%' }} />
                            </div>
                          </div>
                        ) : checkout.error && !checkout.clientSecret ? (
                          <Well UNSAFE_style={styles.errorWell}>
                            <Text UNSAFE_style={styles.errorText}>
                              <strong>{translate('subscription_wizard_checkout_initialization_failed')}</strong> {checkout.errorMessage}
                            </Text>
                            <Button
                              variant="secondary"
                              marginTop="size-200"
                              onPress={() => {
                                // Payment can only resume from a fresh, server-verified checkout
                                resetSubscriptionCheckout()
                                setStep(3)
                              }}
                            >
                              {translate('subscription_wizard_go_back')}
                            </Button>
                          </Well>
                        ) : checkout.clientSecret && stripePromise ? (
                          <Elements
                            key={`${checkout.sellerKey}:${checkout.subscriptionId}`}
                            stripe={stripePromise}
                            options={{
                              clientSecret: checkout.clientSecret,
                              appearance: {
                                theme: 'stripe',
                                variables: { colorPrimary: '#2196F3' },
                              },
                            }}
                          >
                            <PaymentForm
                              checkout={checkout}
                              onPaymentError={setSubscriptionPaymentError}
                              onPaymentSuccess={setSubscriptionPaymentSuccess}
                              setPaymentProcessing={setSubscriptionPaymentProcessing}
                              logTermsAcceptance={logTermsAcceptance}
                              onClose={handleClose}
                              billingEmail={billingDetails.email}
                              selectedLanguage={selectedLanguage}
                              translate={translate}
                            />
                          </Elements>
                        ) : (
                          <Text>{translate('subscription_wizard_initializing_payment')}</Text>
                        )}
                      </>
                    )}
                  </div>
                </Content>

                {/* Footer — navigation buttons (not shown on step 4 when payment form is active) */}
                {step < 4 && (
                  <>
                    <Divider />
                    <Footer>
                      <div style={styles.footerContainer}>
                        <div>
                          {step > 1 && (
                            <Button variant="secondary" onPress={handlePrevStep}>
                              {translate('subscription_wizard_back')}
                            </Button>
                          )}
                        </div>
                        <div style={styles.footerActions}>
                          <Button variant="secondary" onPress={handleClose}>
                            {translate('general_cancel')}
                          </Button>
                          {step < 4 && (
                            <Button
                              variant="cta"
                              onPress={handleNextStep}
                              isDisabled={
                                (step === 2 && (!pricingDetails?.activeDeviceCount || pricingDetails.activeDeviceCount === 0)) ||
                                (step === 3 && !isBillingValid)
                              }
                            >
                            {step === 3 ? translate('subscription_wizard_proceed_to_payment') : translate('subscription_wizard_next')}
                            </Button>
                          )}
                        </div>
                      </div>
                    </Footer>
                  </>
                )}
              </>
            )}
          </>
        </Dialog>
        )}
      </DialogContainer>
    </>
  )
}

// Redux connection
const mapStateToProps = (state: any) => ({
  subscriptionPlans: state.appSettingReducer.subscriptionPlans,
  supportedCurrencies: state.appSettingReducer.supportedCurrencies,
  pricingDetails: state.subscriptionReducer.pricingDetails,
  billingDetails: state.subscriptionReducer.billingDetails,
  selectedCurrency: state.subscriptionReducer.selectedCurrency,
  checkout: state.subscriptionReducer.checkout,
  loading: state.subscriptionReducer.loading,
  error: state.subscriptionReducer.error,
  errorMessage: state.subscriptionReducer.errorMessage,
  selectedLanguage: state.languageReducer.selectedLanguage,
  translate: getTranslate(state.localeReducer),
})

const mapDispatchToProps = (dispatch: any) => ({
  getPricingDetails: () => dispatch(getPricingDetails()),
  updateBillingDetails: (details: Partial<BillingDetails>) => dispatch(updateBillingDetails(details)),
  updateSelectedCurrency: (currency: string) => dispatch(updateSelectedCurrency(currency)),
  initializeSubscriptionCheckout: (billingDetails: BillingDetails, currency: string, clientRequestId: string) =>
    dispatch(initializeSubscriptionCheckout(billingDetails, currency, clientRequestId)),
  resetSubscriptionCheckout: () => dispatch(resetSubscriptionCheckout()),
  setSubscriptionPaymentProcessing: () => dispatch(setSubscriptionPaymentProcessing()),
  setSubscriptionPaymentSuccess: () => dispatch(setSubscriptionPaymentSuccess()),
  setSubscriptionPaymentError: (error: string) => dispatch(setSubscriptionPaymentError(error)),
  logTermsAcceptance: (amount: number, version: string, language: string, subscriptionId?: string | null) =>
    dispatch(logTermsAcceptance(amount, version, language, subscriptionId)),
})

export default connect(mapStateToProps, mapDispatchToProps)(UnifiedSubscriptionWizardComponent)
